import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Sun, Moon, Menu } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useEnvironment } from '../hooks/useEnvironment';
import { formatCountdown } from '../utils/dateUtils';
import { clearCompanyConfigCache } from '../utils/invoiceUtils';
import NotificationsBell from './NotificationsBell';
import { APP_VERSION } from '../version';

// Mapping kept in sync with App.jsx's pageTitleByPath. Duplicated locally
// rather than imported to keep this component self-contained.
const PAGE_TITLES = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/admin': 'Admin Dashboard',
  '/company-config': 'Company Configuration',
  '/manage-users': 'Users',
  '/manage-roles': 'Roles',
  '/assign-permission': 'Assign Permissions',
  '/manage-interest-rates': 'Interest Rates',
  '/manage-loan-reasons': 'Loan Reasons',
  '/manage-loan-expenses': 'Loan Expense Types',
  '/manage-clients': 'Clients',
  '/manage-loans': 'Loans',
  '/repayments': 'Repayments',
  '/manage-loan-contracts': 'Loan Contracts',
  '/messages': 'Messages',
  '/namfisa-reports': 'NAMFISA Reports',
  '/compliance-cases': 'Consumer Protection & Compliance',
  '/audits': 'Audit Trail',
  '/client/profile': 'Client Profile',
  '/client-form': 'Client Form',
  '/loan-application': 'Loan Application',
  '/client-contracts': 'My Loan Contracts',
  '/reports': 'Reports',
  '/support': 'Support Tickets',
  '/change-password': 'Change Password',
};

function resolveTitle(pathname = '') {
  if (!pathname) return '';
  if (pathname.startsWith('/support/support/')) return 'Support Ticket';
  if (pathname.startsWith('/roles/')) return 'Role Permissions';
  return PAGE_TITLES[pathname] || '';
}

const DemoCountdownPill = ({ expiresAt }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = expiresAt - now;
  const expired = remaining <= 0;

  return (
    <div
      role="status"
      aria-live="polite"
      title="You're inside a demo workspace. All clients, loans, repayments and other sample data are wiped on each reset so the next visitor starts clean. Your login email and password are kept — you can sign in again any time and pick up with a fresh workspace."
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border shadow-sm"
      style={{
        background: expired
          ? 'linear-gradient(135deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))'
          : 'linear-gradient(135deg, rgba(251,191,36,0.95), rgba(245,158,11,0.95))',
        color: '#fff',
        borderColor: expired ? 'rgba(127,29,29,0.6)' : 'rgba(146,64,14,0.4)',
      }}
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="uppercase tracking-wider">Demo</span>
      <span aria-hidden="true">·</span>
      <span>
        {expired
          ? 'Sample data wiping now…'
          : <>Sample data resets in <span className="font-mono">{formatCountdown(remaining, 'resetting…')}</span></>}
      </span>
    </div>
  );
};

// onOpenMenu: below lg the sidebar is a drawer; when given, the bar shows the
// button that opens it, to the left of the page title.
const Topbar = ({ onOpenMenu }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const env = useEnvironment();
  const { user, setUser } = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();

  const title = resolveTitle(location?.pathname || '');
  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : '??';

  const logout = () => {
    localStorage.removeItem('token');
    clearCompanyConfigCache();
    setUser(null);
    navigate('/login');
  };

  const expiresAt = useMemo(() => {
    // Countdown restarts at each reset, so prefer last_reset_at; fall back
    // to created_at while the tenant is brand new.
    const anchorRaw = user?.tenant?.resetAnchorAt
      || user?.tenant?.lastResetAt
      || user?.tenant?.createdAt;
    if (!anchorRaw) return null;
    const anchor = Date.parse(anchorRaw);
    if (!Number.isFinite(anchor)) return null;
    const hours = Number(env?.resetIntervalHours) > 0 ? Number(env.resetIntervalHours) : 720;
    return anchor + hours * 60 * 60 * 1000;
  }, [user?.tenant?.resetAnchorAt, user?.tenant?.lastResetAt, user?.tenant?.createdAt, env?.resetIntervalHours]);

  // Show the countdown for every self-registered tenant in any non-production
  // environment (demo/dev/local). The actual data-reset cron still only runs
  // in NODE_ENV=demo, so locally the countdown is purely informational.
  const showCountdown = !!(expiresAt && env?.allowsSelfRegistration);

  // Hide the bar only when not signed in and nothing else needs showing.
  if (!user && !title && !showCountdown && !onOpenMenu) return null;

  return (
    <div className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 md:px-8 py-3 border-b border-slate-200/60 dark:border-slate-700/60 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm">
      <div className="flex items-center gap-3 min-w-0">
        {onOpenMenu && (
          <button
            type="button"
            onClick={onOpenMenu}
            className="lg:hidden flex-shrink-0 p-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors duration-200 flex items-center justify-center"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
        )}
        <h1 className="text-lg sm:text-xl font-bold text-blue-800 dark:text-blue-300 truncate">
          {title || ''}
        </h1>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        {showCountdown && <DemoCountdownPill expiresAt={expiresAt} />}
        {user && (
          <>
            <span
              title={`App version ${APP_VERSION}`}
              className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-600"
            >
              v{APP_VERSION}
            </span>
            <div className="hidden sm:flex items-center gap-2 pl-2">
              <div className="w-7 h-7 rounded-full bg-blue-500/15 dark:bg-blue-500/20 border border-blue-400/40 dark:border-blue-400/30 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-blue-700 dark:text-blue-300">{initials}</span>
              </div>
              <span className="text-xs text-slate-600 dark:text-slate-300 truncate max-w-[180px]">{user.email}</span>
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60 hover:text-amber-500 dark:hover:text-amber-300 transition-colors"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <NotificationsBell />
            <button
              type="button"
              onClick={logout}
              title="Logout"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Topbar;
